import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import Breadcrumb from "../../../../Components/Utils/Breadcrumb";
import { BackButton, CancelButton, SubmitButton } from "../../../../Components/Utils/Buttons";
import { NuInput, NuTextArea } from "../../../../Components/Utils/Input";
import FormikSelectTwo, { FormikAsyncSelect } from "../../../../Components/Utils/Select";
import UseDropDown from "../../../../Components/Hooks/useDropDown";
import UseFetchCurrency from "../../../../Components/Hooks/useFetchCurrencyData";
import useFetchMataData from "../../../../Components/Hooks/useFetchMataData";
import { GetUserDetail } from "../../../../Components/Utils/roles";
import axiosInstance from "../../../../Services/axiosCommon";
import { useToast } from "../../../../Components/Others/ToastServices";
import { useQueryClient } from "@tanstack/react-query";
import Plus from "../../../../Components/Icons/Plus";
import Delete from "../../../../Components/Icons/Delete";

const CreateShippingComparison = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Mode: "with_ypr" or "without_ypr"
  const [comparisonMode, setComparisonMode] = useState("with_ypr");
  const [poOptions, setPoOptions] = useState([]);
  const [isLoadingPrDetails, setIsLoadingPrDetails] = useState(false);

  // Dropdown data sources
  const { value: currencyOptions } = UseFetchCurrency("/currency/drop-down");
  const { data: agentOptions } = UseDropDown("/agents/drop-down");
  const { value: shippingMethods } = useFetchMataData("/dropdown/dropdown-details/", "ShippingMethod");
  const { value: portList } = useFetchMataData("/dropdown/dropdown-details/", "Ports");

  const [agentRows, setAgentRows] = useState([
    { agentId: "", price: "" },
  ]);

  const handleModeChange = (mode) => {
    setComparisonMode(mode);
    if (mode === "without_ypr") {
      formik.setFieldValue("prId", "");
      formik.setFieldValue("poId", "");
      setPoOptions([]);
    }
  };

  const handlePrSelect = async (selectedOption) => {
    const prId = selectedOption?.value || "";
    formik.setFieldValue("prId", prId);
    formik.setFieldValue("poId", "");
    setPoOptions([]);

    if (!prId) return;

    setIsLoadingPrDetails(true);
    try {
      const [prRes, poRes] = await Promise.allSettled([
        axiosInstance.get(`/PR/${prId}`),
        axiosInstance.get(`/PO/drop-down/by-PR/${prId}`),
      ]);

      if (prRes.status === "fulfilled") {
        const prData = prRes.value.data?.data || prRes.value.data;
        const desc = prData?.description || prData?.prDescription || "";
        if (desc) {
          formik.setFieldValue("description", desc);
        }
      }

      if (poRes.status === "fulfilled") {
        const poData = poRes.value.data?.data || poRes.value.data || [];
        const formatted = Array.isArray(poData)
          ? poData.map((po) => ({
            value: po.id !== undefined ? po.id : po.value,
            label: po.poNumber || po.poNo || po.ref || po.name || po.label || `PO #${po.id || po.value}`,
          }))
          : [];
        setPoOptions(formatted);
      }
    } catch (err) {
      console.error("Error fetching PR / PO details:", err);
    } finally {
      setIsLoadingPrDetails(false);
    }
  };

  const handleAddAgentRow = () => {
    setAgentRows((prev) => [...prev, { agentId: "", price: "" }]);
  };

  const handleRemoveAgentRow = (index) => {
    if (agentRows.length <= 1) {
      toast.open({
        type: "warning",
        message: "Warning",
        description: "At least one agent quote is required.",
      });
      return;
    }
    setAgentRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAgentChange = (index, field, val) => {
    setAgentRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: val };
      return updated;
    });
  };

  const formik = useFormik({
    initialValues: {
      prId: "",
      poId: "",
      description: "",
      port: "",
      destination: "",
      shipmenttype: "",
      currencyId: "",
    },
    validationSchema: yup.object({
      prId:
        comparisonMode === "with_ypr"
          ? yup.string().required("Purchase Request (YPR) is required!")
          : yup.string().nullable(),
      poId:
        comparisonMode === "with_ypr"
          ? yup.string().required("Purchase Order (PO) is required!")
          : yup.string().nullable(),
      description: yup.string().required("Description is required!"),
      shipmenttype: yup.string().required("Shipment Type is required!"),
      port: yup.string().required("Port of Loading is required!"),
      destination: yup.string().required("Destination Port is required!"),
    }),
    onSubmit: async (values) => {
      const validAgents = agentRows
        .filter((row) => row.agentId && row.price !== "")
        .map((row) => ({
          agentId: Number(row.agentId),
          price: parseFloat(row.price),
        }));

      if (validAgents.length === 0) {
        toast.open({
          type: "warning",
          message: "Validation Error",
          description: "Please specify at least one agent with a valid price.",
        });
        return;
      }

      const user = GetUserDetail();
      const updatedBy = user?.id || user?.userId || 1;

      const payload = {
        prId: comparisonMode === "with_ypr" && values.prId ? Number(values.prId) : null,
        poId: comparisonMode === "with_ypr" && values.poId ? Number(values.poId) : null,
        currencyId: values.currencyId ? Number(values.currencyId) : null,
        shipmenttype: values.shipmenttype || "",
        port: values.port || "",
        destination: values.destination || "",
        description: values.description || "",
        agents: validAgents,
        updatedBy: updatedBy,
      };

      setIsSubmitting(true);
      try {
        const response = await axiosInstance.post(
          "/shipping-comparison/add",
          payload
        );

        if (response?.data?.status || response?.status === 200 || response?.status === 201) {
          toast.open({
            type: "success",
            message: "Success",
            description: "Shipping comparison created successfully.",
          });
          queryClient.invalidateQueries(["allShippingComparisons"]);
          navigate("/shipping/shipping-comparison");
        } else {
          toast.open({
            type: "error",
            message: "Error",
            description: response?.data?.message || "Failed to create shipping comparison.",
          });
        }
      } catch (error) {
        console.error("Error creating shipping comparison:", error);
        toast.open({
          type: "error",
          message: "Error",
          description:
            error?.response?.data?.message ||
            "An error occurred while creating comparison.",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <div className="w-full h-full p-2 lg:px-4">
      {/* Top Bar */}
      <div className="w-full flex flex-row justify-between items-center gap-2 mb-3">
        <Breadcrumb
          items={[
            { name: "Shipping", link: "/shipping/shipping-comparison" },
            { name: "Shipping Comparison", link: "/shipping/shipping-comparison" },
            { name: "Create Shipping Comparison" },
          ]}
        />
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link to="/shipping/shipping-comparison">
            <BackButton link={true} />
          </Link>
        </div>
      </div>

      {/* Form Container */}
      <div className="w-full min-h-[calc(100vh-140px)] h-auto md:h-[calc(100vh-140px)] overflow-y-auto">
        <form onSubmit={formik.handleSubmit}>
          <div className="w-full mx-auto bg-white rounded-md pb-4">
            <div className="w-full h-full flex justify-start content-start flex-wrap px-2 mb-4">

              {/* Section 1: Comparison Details */}
              <div className="w-full">
                <p className="pb-2 pt-4 px-4 text-sm font-medium text-[#111537] underline decoration-[#048178] decoration-2 underline-offset-4">
                  Comparison Details :
                </p>
              </div>

              {/* Mode Selection */}
              <div className="w-full px-4 py-2 flex items-center gap-6">
                <label className="flex items-center gap-2 text-xs font-medium text-[#111537] cursor-pointer">
                  <input
                    type="radio"
                    name="comparisonMode"
                    value="with_ypr"
                    checked={comparisonMode === "with_ypr"}
                    onChange={() => handleModeChange("with_ypr")}
                    className="w-4 h-4 text-[#048178] focus:ring-[#048178] accent-[#048178]"
                  />
                  With YPR
                </label>
                <label className="flex items-center gap-2 text-xs font-medium text-[#111537] cursor-pointer">
                  <input
                    type="radio"
                    name="comparisonMode"
                    value="without_ypr"
                    checked={comparisonMode === "without_ypr"}
                    onChange={() => handleModeChange("without_ypr")}
                    className="w-4 h-4 text-[#048178] focus:ring-[#048178] accent-[#048178]"
                  />
                  Without YPR
                </label>
              </div>

              {/* With YPR fields */}
              {comparisonMode === "with_ypr" && (
                <>
                  <FormikAsyncSelect
                    label="Purchase Request (YPR)"
                    name="prId"
                    formik={formik}
                    placeholder="Choose YPR"
                    url="/PR/drop-down"
                    isRequired={true}
                    width="w-full md:w-1/2 lg:w-1/3"
                    onChange={(opt) => handlePrSelect(opt)}
                  />
                  <FormikSelectTwo
                    label="Purchase Order"
                    name="poId"
                    formik={formik}
                    placeholder={
                      !formik.values.prId
                        ? "Select YPR first"
                        : poOptions.length === 0
                          ? "No POs found"
                          : "Choose PO"
                    }
                    Options={poOptions}
                    disabled={!formik.values.prId}
                    isRequired={true}
                    width="w-full md:w-1/2 lg:w-1/3"
                  />
                </>
              )}

              <FormikSelectTwo
                label="Currency"
                name="currencyId"
                formik={formik}
                placeholder="Choose"
                Options={currencyOptions || []}
                width="w-full md:w-1/2 lg:w-1/3"
              />

              <FormikSelectTwo
                label="Shipment Type"
                name="shipmenttype"
                formik={formik}
                placeholder="Choose"
                Options={shippingMethods || []}
                isRequired={true}
                width="w-full md:w-1/2 lg:w-1/3"
              />

              <FormikSelectTwo
                label="Port of Loading"
                name="port"
                formik={formik}
                placeholder="Choose"
                Options={portList || []}
                isRequired={true}
                width="w-full md:w-1/2 lg:w-1/3"
              />

              <FormikSelectTwo
                label="Destination Port"
                name="destination"
                formik={formik}
                placeholder="Choose"
                Options={portList || []}
                isRequired={true}
                width="w-full md:w-1/2 lg:w-1/3"
              />

              <NuInput
                label="Description"
                type="text"
                formik={formik}
                placeholder="Description"
                name="description"
                isRequired={true}
                width="w-full md:w-1/2 lg:w-2/3"
              />

              {/* Section 2: Agent Quotations */}
              <div className="w-full mt-2">
                <div className="flex justify-between items-center pr-4">
                  <p className="pb-2 pt-4 px-4 text-sm font-medium text-[#111537] underline decoration-[#048178] decoration-2 underline-offset-4">
                    Agent Quotations :
                  </p>
                  <button
                    type="button"
                    onClick={handleAddAgentRow}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#048178] text-white text-xs font-medium rounded-md hover:bg-[#036962] transition-colors cursor-pointer"
                  >
                    <Plus /> Add Agent
                  </button>
                </div>
              </div>

              <div className="w-full px-4 mt-2 overflow-x-auto">
                <table className="w-full border-collapse border border-gray-200 rounded-md">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-[#111537]">
                      <th className="py-2.5 px-3 text-left w-12">S.No</th>
                      <th className="py-2.5 px-3 text-left w-1/2">Shipping Agent *</th>
                      <th className="py-2.5 px-3 text-left w-1/3">Quoted Price *</th>
                      <th className="py-2.5 px-3 text-center w-16">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-xs">
                    {agentRows.map((row, index) => (
                      <tr key={index} className="odd:bg-white even:bg-slate-50">
                        <td className="py-2.5 px-3 text-gray-500 font-medium">
                          {index + 1}
                        </td>
                        <td className="py-2 px-3">
                          <select
                            value={row.agentId}
                            onChange={(e) =>
                              handleAgentChange(index, "agentId", e.target.value)
                            }
                            className="w-full h-[36px] px-2.5 text-xs text-gray-700 bg-white border border-gray-300 rounded-md focus:outline-none focus:border-[#048178]"
                          >
                            <option value="">Select Agent</option>
                            {agentOptions?.map((agent) => (
                              <option
                                key={agent.id || agent.value}
                                value={agent.id || agent.value}
                              >
                                {agent.companyName || agent.name || agent.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="py-2 px-3">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={row.price}
                            onChange={(e) =>
                              handleAgentChange(index, "price", e.target.value)
                            }
                            placeholder="0.00"
                            className="w-full h-[36px] px-2.5 text-xs text-gray-700 bg-white border border-gray-300 rounded-md focus:outline-none focus:border-[#048178]"
                          />
                        </td>
                        <td className="py-2 px-3 text-center">
                          <button
                            type="button"
                            onClick={() => handleRemoveAgentRow(index)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors cursor-pointer"
                            title="Remove Agent"
                          >
                            <Delete />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Bottom Submit / Cancel Buttons */}
              <div className="w-full mx-auto my-4 mt-8 px-4">
                <div className="flex justify-end gap-4">
                  <CancelButton handleClick={() => navigate("/shipping/shipping-comparison")} />
                  <SubmitButton isLoading={isSubmitting} />
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateShippingComparison;
